import { useEffect, useRef, useState } from "react";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

export const CountUp = ({
  to,
  from = 0,
  duration = 1200,
  prefix = "",
  suffix = "",
  decimals = 0,
  separator = ".",
  className = "",
  trigger = true, // start when true
}) => {
  const [val, setVal] = useState(from);
  const startedRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!trigger || startedRef.current) return;
    startedRef.current = true;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = easeOutCubic(t);
      const current = from + (to - from) * eased;
      setVal(current);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [trigger, from, to, duration]);

  const fixed = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toString();
  const [int, dec] = fixed.split(".");
  const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  const display = dec ? `${withSep},${dec}` : withSep;
  return <span className={className}>{prefix}{display}{suffix}</span>;
};

// Animate when element enters viewport
export const useInView = (options = { threshold: 0.2, once: true }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        if (options.once && ref.current) obs.unobserve(ref.current);
      } else if (!options.once) {
        setInView(false);
      }
    }, { threshold: options.threshold ?? 0.2 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [options.threshold, options.once]);
  return [ref, inView];
};

// Wrapper that fades+translates in when entering viewport
export const FadeInUp = ({
  children,
  delay = 0,
  duration = 600,
  className = "",
  as: Tag = "div",
  ...rest
}) => {
  const [ref, inView] = useInView({ threshold: 0.15, once: true });
  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(20px)",
        transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};
